<script lang="ts">
  import { _ as t } from "svelte-i18n";
  import { onMount } from "svelte";
  import { getContainerStyle } from "../../libs/layout";
  import { storageModel, StorageKeys, LangEnums } from "../lib/storage-manager";
  import { bridge } from "../lib/bridge";
  import { isEmbedded, isIos } from "../../libs/chief-bridge";
  import Logo from "../assets/images/logo.png";

  // 根据环境自动切换基础 URL
  const commonH5Url =
    __MODE__ === "prod"
      ? "https://common-h5.chiefgroup.com.hk/"
      : "https://common-h5.test.stx365.com/";

  // 简体中文用 sc，其他语言统一用 tc
  const dir =
    storageModel[StorageKeys.language] === LangEnums.zhCN ? "sc" : "tc";
  const imgs = import.meta.glob("../assets/images/**/*.png", {
    eager: true,
    import: "default",
  }) as Record<string, string>;
  const img = (name: string) => imgs[`../assets/images/${dir}/${name}.png`];

  const Bg1 = img("bg");
  const Activity1 = img("activity-1");
  const Activity2 = img("activity-2");
  const Activity3 = img("activity-3");

  let tabActive = $state(1);
  const tabs = [
    { key: 1, label: "tab_investment_carnival" },
    { key: 2, label: "tab_super_birthday_reward" },
    { key: 3, label: "tab_chief_airdrop" },
  ];

  /** 获取 URL 参数 from_channel */
  function getFromChannel(): string {
    return (
      new URLSearchParams(window.location.search).get("from_channel") || ""
    );
  }

  /** 按钮名称映射 */
  const btnNames: Record<number, string> = {
    1: "立即报名",
    2: "立即参与",
    3: "立即测试",
  };

  // 页面浏览埋点
  onMount(() => {
    bridge.sendDatafinder("page_view", {
      page_name: "47周年活动主页",
      scenes: "47周年活动主页浏览",
      H5_title: "47周年活动主页",
      H5_url: window.location.href,
      from_channel: getFromChannel(),
      p_id: "anniversary-main",
      m_id: "w",
      t_id: "w",
      s_id: "w",
      e_id: "w",
    });
  });

  // 切换tab
  const handleTabs = (key: number) => {
    tabActive = key;
    const element = document.getElementById(`activity-${key}`);
    if (element) {
      const container = element.closest(".nav-content");
      if (container) {
        const top = element.offsetTop - 120;
        container.scrollTo({ top, behavior: "smooth" });
      } else {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  /** 活动跳转路径映射 */
  const activityPaths: Record<number, { path: string; sellPoint?: string }> = {
    2: { path: "lottery-anniversary/", sellPoint: "mainhall" },
  };

  /** 跳转到子活动页 */
  function navigateToActivity(config: { path: string; sellPoint?: string }) {
    const params = new URLSearchParams();
    // sellPoint 有值时写死，否则从 URL 取或用默认值
    const channel =
      config.sellPoint ?? (getFromChannel() || "anniversary-main");
    params.set("from_channel", channel);
    const targetUrl = `${commonH5Url}${config.path}?${params.toString()}`;
    if (!isEmbedded()) {
      window.location.href = targetUrl;
    } else {
      const linkUrl = isIos()
        ? `chiefAppNav://web?url=${encodeURIComponent(targetUrl)}&displayNav=0`
        : `com.app.ui.nav.HomeScreen$WebViewCompose@${JSON.stringify({ showTitle: "", webType: "Other", url: targetUrl, displayNav: 0, showChiefLoading: "false" })}`;
      bridge.callNative("openLink", { linkType: 3, linkUrl });
    }
  }

  // 点击活动
  const handleActivity = (key: number) => {
    // 元素点击埋点
    bridge.sendDatafinder("element_click", {
      page_name: "47周年活动主页",
      scenes: "47周年活动主页点击",
      element_name: "任务点击",
      H5_title: "47周年活动主页",
      from_channel: getFromChannel(),
      p_id: "anniversary-main",
      m_id: "w",
      t_id: "w",
      s_id: "w",
      e_id: btnNames[key] || `活动${key}`,
    });

    const path = activityPaths[key];
    if (path) {
      navigateToActivity(path);
    }
  };
</script>

<div
  class="min-h-screen w-full bg-[#0E2C8C] relative text-text-reverse1 pb-[50px]"
  style={getContainerStyle()}
>
  <div>
    <!-- logo -->
    {#if !isEmbedded()}
      <img
        src={Logo}
        alt=""
        class="w-[122px] absolute left-1/2 top-[40px] -translate-x-1/2"
      />
    {/if}
    <!-- 背景图 -->
    <img src={Bg1} alt="" class="w-full" />
  </div>
  <!-- 内容区域 -->
  <div class="px-[16px]">
    <!-- 选项卡 -->
    <div class="-mt-[70px]">
      <div
        class="text-center h-[26px] px-[16px] flex items-center justify-center rounded-[8px] mb-[12px] text-12"
        style="background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.15) 58.65%, rgba(255, 255, 255, 0) 100%); backdrop-filter: blur(12px);"
      >
        {$t("code")}
      </div>
      <div
        class=" flex items-center rounded-[8px] border border-[#1A3DAD] bg-[#001A62] p-[6px] mb-[15px]"
      >
        {#each tabs as tab (tab.key)}
          <button
            class="flex-1 rounded-[6px] h-[34px] text-center text-[16px] font-semibold transition-all text-[#91A6E9]"
            class:bg-[#FED804]={tabActive === tab.key}
            class:!text-[#0F2F8A]={tabActive === tab.key}
            class:text-white={tabActive !== tab.key}
            onclick={() => handleTabs(tab.key)}
          >
            {$t(tab.label)}
          </button>
        {/each}
      </div>
    </div>
    <!-- 活动区域 -->
    <div class="flex flex-col gap-[40px]">
      <div class="min-h-screen flex flex-col gap-[40px]">
        <!-- 活动1 -->
        <div class="relative scroll-mt-[15px]" id="activity-1">
          <img src={Activity1} alt="" class="w-full" />
        </div>
        <!-- 活动2 -->
        <div class="relative scroll-mt-[15px]" id="activity-2">
          <img src={Activity2} alt="" class="w-full" />
          <button
            type="button"
            aria-label="活动2"
            class="absolute left-1/2 bottom-[75px] h-[50px] -translate-x-1/2 w-[calc(100%-60px)]"
            onclick={() => handleActivity(2)}
          ></button>
        </div>
        <!-- 活动3 -->
        <div class="relative scroll-mt-[15px]" id="activity-3">
          <img src={Activity3} alt="" class="w-full" />
        </div>
      </div>
      <!-- 免责声明 -->
      <div class="text-[10px]">
        <p>{$t("disclaimers")}</p>
        <p>{$t("disclaimers_text")}</p>
      </div>
    </div>
  </div>
</div>
